import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import {
  MONTHLY_RECURRING,
  MONTHLY_RECURRING_LABEL,
  QUARTERLY_RECURRING,
  QUARTERLY_RECURRING_LABEL,
  WEEKLY_RECURRING,
  WEEKLY_RECURRING_LABEL,
  YEARLY_RECURRING,
  YEARLY_RECURRING_LABEL,
} from "constants/invoicing";
import dayjs from "dayjs";
import { Field, FieldProps, Form, Formik } from "formik";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import {
  Invoice,
  RecurringSettingTypes,
  useCreateInvoiceRecurringMutation,
  useUpdateInvoiceRecurringMutation,
} from "store/apis/invoices";

type RecurringInvoicePayload = {
  endDate?: string;
  repeat: RecurringSettingTypes;
  sendInvoiceAutomatically: boolean;
};

export const RecurringInvoiceSetting = ({
  isOpen,
  close,
  invoice,
  entityId,
}: {
  isOpen: boolean;
  close: () => void;
  invoice: Invoice;
  entityId: string;
}) => {
  const [createInvoiceRecurring, { isLoading }] =
    useCreateInvoiceRecurringMutation();
  const [updateInvoiceRecurring, { isLoading: isUpdating }] =
    useUpdateInvoiceRecurringMutation();
  const { successToast, alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();

  const onRecurringInvoiceUpdate = async (values: RecurringInvoicePayload) => {
    if (groupId && invoice?.invoice_settings?.uuid) {
      try {
        const payload = {
          is_recurring_enabled: true,
          recurring_setting_type: values.repeat,
          recurring_end_date: values.endDate ? values.endDate : null,
          is_recurring_send_automated: values.sendInvoiceAutomatically,
        };
        await updateInvoiceRecurring({
          entityId,
          groupId,
          invoiceId: invoice.uuid,
          payload,
          invoiceSettingId: invoice.invoice_settings.uuid,
        }).unwrap();
        successToast({
          message: "Invoice recurring enabled",
        });
        close();
      } catch (error: any) {
        alertToast({ message: error?.data?.error?.message });
      }
    }
  };

  const onRecurringInvoiceAdd = async (values: RecurringInvoicePayload) => {
    if (groupId) {
      try {
        const payload = {
          is_recurring_enabled: true,
          recurring_setting_type: values.repeat,
          recurring_end_date: values.endDate ? values.endDate : null,
          is_recurring_send_automated: values.sendInvoiceAutomatically,
        };
        await createInvoiceRecurring({
          entityId,
          groupId,
          invoiceId: invoice.uuid,
          payload,
        }).unwrap();
        successToast({
          message: "Invoice recurring enabled",
        });
        close();
      } catch (error: any) {
        alertToast({ message: error?.data?.error?.message });
      }
    }
  };

  const repeatOptions = [
    { label: WEEKLY_RECURRING_LABEL, value: WEEKLY_RECURRING },
    { label: MONTHLY_RECURRING_LABEL, value: MONTHLY_RECURRING },
    { label: QUARTERLY_RECURRING_LABEL, value: QUARTERLY_RECURRING },
    { label: YEARLY_RECURRING_LABEL, value: YEARLY_RECURRING },
  ];

  const getNextInvoiceDate = (selectedOption: RecurringSettingTypes) => {
    switch (selectedOption) {
      case WEEKLY_RECURRING: {
        return dayjs(invoice.invoice_date).add(1, "week");
      }
      case MONTHLY_RECURRING: {
        return dayjs(invoice.invoice_date).add(1, "month");
      }
      case QUARTERLY_RECURRING: {
        return dayjs(invoice.invoice_date).add(3, "month");
      }
      case YEARLY_RECURRING: {
        return dayjs(invoice.invoice_date).add(1, "year");
      }
      default: {
        return dayjs(invoice.invoice_date).add(1, "month");
      }
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
      <Formik
        initialValues={{
          endDate: invoice?.invoice_settings?.recurring_end_date || "",
          repeat:
            invoice?.invoice_settings?.recurring_setting_types ||
            MONTHLY_RECURRING,
          sendInvoiceAutomatically:
            invoice?.invoice_settings?.is_recurring_send_automated || true,
        }}
        onSubmit={
          invoice?.invoice_settings?.uuid
            ? onRecurringInvoiceUpdate
            : onRecurringInvoiceAdd
        }
      >
        {({ submitForm, values, setFieldValue }) => {
          const selectedOption = repeatOptions.find(
            ({ value }) => value === values.repeat
          );
          let nextRecurringDate = getNextInvoiceDate(values.repeat).format(
            DISPLAY_DATE
          );
          let recurringNote = `The next invoice will be auto-generated with the date ${nextRecurringDate}`;

          return (
            <Modal.Content useCustomOverlay>
              <Modal.Header>
                <Modal.Title>Make invoice recurring</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body>
                <Form className="t-m-0 t-space-y-2">
                  <Combobox
                    name="repeat"
                    components={{ ClearIndicator: () => null }}
                    required
                    withForm
                    label="Repeat"
                    options={repeatOptions}
                    menuPortalTarget={document.body}
                    note={recurringNote}
                    value={selectedOption}
                  />
                  <div>
                    <Field name="endDate" placeholder={DISPLAY_DATE} required>
                      {({ field }: FieldProps) => {
                        return (
                          <DateInput
                            {...field}
                            saveFormat={YYYY_MM_DD}
                            label="End date"
                            placeholder={DISPLAY_DATE}
                            block
                            minDate={new Date(nextRecurringDate)}
                          />
                        );
                      }}
                    </Field>
                  </div>
                  <div className="t-flex t-gap-2 t-items-center">
                    <Switch
                      checked={values.sendInvoiceAutomatically}
                      size="small"
                      name="sendInvoiceAutomatically"
                      onCheckedChange={(value: boolean) =>
                        setFieldValue("sendInvoiceAutomatically", value)
                      }
                    />
                    <span className="t-text-subtext-sm">
                      Automatically send invoice email to the customer
                    </span>
                  </div>
                </Form>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Button type="button" onClick={close}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  isLoading={isLoading || isUpdating}
                  disabled={isLoading || isUpdating}
                  customType="primary"
                  onClick={submitForm}
                >
                  Save
                </Button>
              </Modal.FooterButtonGroup>
            </Modal.Content>
          );
        }}
      </Formik>
    </Modal.Root>
  );
};
